import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { faArrowUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Row from "antd/es/row";
import { addDays, isPast, subDays } from "date-fns/esm";
import { useIntl } from "react-intl";

import { EntityTypesSupportingDownloading } from "@mapmycustomers/shared/types/entity";
import PlatformBounds from "@mapmycustomers/shared/types/map/PlatformBounds";
import User from "@mapmycustomers/shared/types/User";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { Alert, ButtonLink } from "@mapmycustomers/ui";

import DownloadEntitiesModal from "@app/component/DownloadEntitiesModal";
import TextWithLine from "@app/component/typography/TextWithLine";
import Path from "@app/enum/Path";
import ActivityRepsCard from "@app/scene/reports/components/Activity/components/ActivityReps";
import ActivityTypesCard from "@app/scene/reports/components/Activity/components/ActivityTypes";
import { areCheckInFieldsVisible } from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";

import MapFilter, { getEntityTypeByMapFilter } from "../../enums/MapFilter";
import { getFeaturePathByMapFilter, reportsActivityAnalytics } from "../../reportsAnalytics";
import {
  downloadActivityReportData,
  fetchActivityCardData,
  fetchActivityCardListData,
  setDateRange,
} from "../../store/actions";
import ActivityCardData from "../../store/ActivityCardData";
import { getActivityCardData, getDateRange, isLoading } from "../../store/selectors";
import DateRange from "../../types/DateRange";
import useUserIds from "../../utils/useUserIds";
import BaseScreenProps from "../BaseScreenProps";
import ActivitiesMapFilter from "../MapFilters/ActivityMapFilter";

import styles from "./ActivityCard.module.scss";
import ActivityReportHeader from "./components/ActivityReportHeader";
import ActivityReportMap from "./components/ActivityReportMap";
import PinList from "./components/PinList";

interface Props extends BaseScreenProps {
  activityCardData: ActivityCardData;
  checkInFieldsVisibility: boolean;
  dateRange: DateRange;
  downloadActivityReportData: typeof downloadActivityReportData;
  onFetchActivityCardData: typeof fetchActivityCardData.request;
  onFetchActivityCardListData: typeof fetchActivityCardListData.request;
  setDateRange: (dateRange: DateRange) => void;
}

const ActivityCard: React.FC<Props> = ({
  activityCardData,
  checkInFieldsVisibility,
  dateRange,
  downloadActivityReportData,
  onFetchActivityCardData,
  onFetchActivityCardListData,
  selectedUsers,
  setDateRange,
}) => {
  const intl = useIntl();

  const userIds: Array<User["id"]> = useUserIds(selectedUsers);
  const [selectedEntityTypeForDownloading, setSelectedEntityTypeForDownloading] = useState<
    EntityTypesSupportingDownloading | undefined
  >();

  const [mapFilter, setMapFilter] = useState<MapFilter>(MapFilter.COMPLETED_ACTIVITIES);

  useChangeTracking(
    () =>
      reportsActivityAnalytics.clicked(["Smart Filter", ...getFeaturePathByMapFilter(mapFilter)]),
    [mapFilter]
  );

  const handleMapFilterChange = useCallback(
    (mapFilter: MapFilter) => {
      // if it was an UPCOMING_ACTIVITIES filter, ensure that current date range
      // is not in a past. And if it is, make it be 30 days from now or at least
      // to start from now (when start date was in a past and end date was in a future).
      if (mapFilter === MapFilter.UPCOMING_ACTIVITIES) {
        const startDate = isPast(dateRange.startDate) ? new Date() : dateRange.startDate;
        const endDate = isPast(dateRange.endDate) ? addDays(Date.now(), 30) : dateRange.endDate;
        if (startDate !== dateRange.startDate || endDate !== dateRange.endDate) {
          setDateRange({ endDate, startDate });
        }
      } else {
        // if switching back to other map filters from upcoming activity filter
        // Both dates need to be in past.
        const startDate = isPast(dateRange.startDate)
          ? dateRange.startDate
          : subDays(Date.now(), 30);
        const endDate = isPast(dateRange.endDate) ? dateRange.endDate : new Date();
        if (startDate !== dateRange.startDate || endDate !== dateRange.endDate) {
          setDateRange({ endDate, startDate });
        }
      }
      setMapFilter(mapFilter);
    },
    [dateRange, setDateRange, setMapFilter]
  );

  // fetch all data needed for page (user info activities, groups, etc.)
  const loadActivityCardData = useCallback(
    () => {
      onFetchActivityCardData({ dateRange, mapFilter, userIds });
      // initial calling of entity list
      onFetchActivityCardListData(0);
    },
    // disabled deps linting coz it fails to understand a complex expression with getTime()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      dateRange.startDate.getTime(),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      dateRange.endDate.getTime(),
      onFetchActivityCardData,
      onFetchActivityCardListData,
      mapFilter,
      userIds,
    ]
  );

  // fetch all data needed for page (user info activities, groups, etc.)
  const handleUpdateBounds = useCallback(
    (bounds?: PlatformBounds, zoom?: number) => onFetchActivityCardData({ bounds, zoom }),
    [onFetchActivityCardData]
  );

  useEffect(() => {
    loadActivityCardData();
  }, [loadActivityCardData]);

  const handleDownload = useCallback(
    (columns: ColumnModel) => {
      if (selectedEntityTypeForDownloading) {
        downloadActivityReportData({ columns, entityType: selectedEntityTypeForDownloading });
      }
    },
    [downloadActivityReportData, selectedEntityTypeForDownloading]
  );

  return (
    <div className={styles.container}>
      <ActivityReportHeader
        activityCardData={activityCardData}
        dateRange={dateRange}
        filter={mapFilter}
      >
        <TextWithLine>
          {intl.formatMessage({
            id: "reports.activities.charts",
            defaultMessage: "Charts",
            description: "Title for Charts section on Activities screen.",
          })}
        </TextWithLine>

        <Alert
          action={
            <ButtonLink className={styles.open} href={`${Path.DASHBOARD}`} target="_blank">
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              {intl.formatMessage({
                id: "reports.activities.openPerformanceProfiles.button",
                defaultMessage: "Open Performance Profiles",
                description: "Alert button to open Performance Profiles",
              })}
            </ButtonLink>
          }
          className={styles.alert}
          message={intl.formatMessage({
            id: "reports.activities.performanceProfiles.alert.title",
            defaultMessage:
              "Looking for more charts? Go to Performance Profiles to drill into org-wide performance metrics.",
            description: "Performance profiles open alert",
          })}
          showIcon
          type="info"
        />
        <div className={styles.charts}>
          <ActivityRepsCard selectedUsers={selectedUsers} />
          <ActivityTypesCard selectedUsers={selectedUsers} />
        </div>
      </ActivityReportHeader>

      <Row className={styles.mapContainer}>
        <Col lg={{ span: 16 }} xl={{ span: 18 }}>
          <ActivityReportMap
            filter={mapFilter}
            onChangeBounds={handleUpdateBounds}
            pins={activityCardData.pins}
          />
        </Col>
        <Col className={styles.filterContainer} lg={{ span: 8 }} xl={{ span: 6 }}>
          <ActivitiesMapFilter
            checkInFieldsVisibility={checkInFieldsVisibility}
            filter={mapFilter}
            onChange={handleMapFilterChange}
            onDownload={(filter) =>
              setSelectedEntityTypeForDownloading(
                getEntityTypeByMapFilter(filter) as EntityTypesSupportingDownloading
              )
            }
          />
        </Col>
      </Row>
      <Row className={styles.entityListContainer}>
        <Col lg={{ span: 24 }} xl={{ span: 18 }}>
          <PinList activityCardData={activityCardData} filter={mapFilter} />
        </Col>
      </Row>
      {!!selectedEntityTypeForDownloading && (
        <DownloadEntitiesModal
          entityType={selectedEntityTypeForDownloading}
          hasAdditionalFilters
          informationPoint={intl.formatMessage({
            id: "activityList.export.point1",
            defaultMessage: "All activities in view",
            description: "Information point 1 for export on activity list",
          })}
          onDownload={handleDownload}
          onHide={() => setSelectedEntityTypeForDownloading(undefined)}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityCardData: getActivityCardData(state),
  checkInFieldsVisibility: areCheckInFieldsVisible(state),
  dateRange: getDateRange(state),
  loading: isLoading(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  downloadActivityReportData,
  onFetchActivityCardData: fetchActivityCardData.request,
  onFetchActivityCardListData: fetchActivityCardListData.request,
  setDateRange,
};

export default connect(mapStateToProps, mapDispatchToProps)(ActivityCard);
